// Expense categories and the month/year views both apps render. The
// category list and the standalone rule mirror api/internal/service/expenses.go
// — keep them in step.

export const EXPENSE_CATEGORIES = [
  { key: "food", label: "Food & dining" },
  { key: "shopping", label: "Shopping & grooming" },
  { key: "health", label: "Health & fitness" },
  { key: "commute", label: "Commute" },
  { key: "bills", label: "Subscriptions & bills" },
  { key: "electronics", label: "Electronics" },
  { key: "travel", label: "Travel" },
  { key: "housing", label: "Basic house cost" },
  { key: "misc", label: "Miscellaneous" },
  { key: "family", label: "Family" },
  { key: "vehicle", label: "Car" },
  { key: "insurance", label: "Insurance" },
];

const LABEL = Object.fromEntries(EXPENSE_CATEGORIES.map((c) => [c.key, c.label]));
export const categoryLabel = (key) => LABEL[key] || key;

/** Categories shown as their own row on the month page; the rest fold into General spending. */
export const STANDALONE_CATEGORIES = new Set(["family", "vehicle", "insurance"]);
export const isStandalone = (category) => STANDALONE_CATEGORIES.has(category);

export const MONTHS_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
export const MONTHS_LONG = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const pad2 = (n) => String(n).padStart(2, "0");
export const monthKey = (year, month) => `${year}-${pad2(month)}`; // month 1..12
export const monthOfDate = (date) => (date || "").slice(0, 7);
/** Today as YYYY-MM-DD in the device's own timezone. (toISOString would give
 *  the UTC day, which in India is still yesterday until 05:30.) */
export const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
};
const sum = (rows) => rows.reduce((s, r) => s + (r.amount || 0), 0);

/** "31 Oct 2026" from "2026-10-31". */
export function formatDay(date, { year = true } = {}) {
  if (!date) return "";
  const [y, m, d] = date.split("-");
  return `${Number(d)} ${MONTHS_SHORT[Number(m) - 1]}${year ? ` ${y}` : ""}`;
}

/** "On the 1st" / "On the 20th" / "Month end" for a recurring rule's day. */
export function recurringWhen(day) {
  if (day >= 28) return "Month end";
  const s = day % 10 === 1 && day !== 11 ? "st" : day % 10 === 2 && day !== 12 ? "nd" : day % 10 === 3 && day !== 13 ? "rd" : "th";
  return `On the ${day}${s}`;
}

/** The date a rule falls on in a month (day clamped to the month's length). */
export function recurringDueDate(rule, year, month) {
  const last = new Date(Date.UTC(year, month, 0)).getUTCDate();
  return `${year}-${pad2(month)}-${pad2(Math.min(rule.day, last))}`;
}

/**
 * Splits a year's data into the month a page shows: General spending
 * (every non-standalone entry outside a group), the groups counted in the
 * month, the standalone entries, and the recurring entries still to come.
 * `data` is the GET /expenses payload; `month` is 1..12.
 */
export function monthView(data, year, month) {
  const key = monthKey(year, month);
  const expenses = data?.expenses || [];
  const groups = (data?.groups || []).filter((g) => g.month === key);
  const inMonth = expenses.filter((e) => !e.group_id && monthOfDate(e.date) === key);
  const general = inMonth.filter((e) => !isStandalone(e.category));
  const singles = inMonth.filter((e) => isStandalone(e.category)).sort((a, b) => b.amount - a.amount);
  const groupRows = groups
    .map((g) => {
      const items = expenses.filter((e) => e.group_id === g.id).sort((a, b) => (a.date < b.date ? -1 : 1));
      return { group: g, items, total: sum(items) };
    })
    .sort((a, b) => b.total - a.total);
  const total = sum(general) + sum(singles) + groupRows.reduce((s, g) => s + g.total, 0);
  const today = todayISO(); // the device's day, not the server's UTC one
  const names = new Set(inMonth.map((e) => e.name.trim().toLowerCase()));
  const upcoming = (data?.recurring || [])
    .map((r) => ({ rule: r, date: recurringDueDate(r, year, month) }))
    .filter(({ rule, date }) => date >= today && !names.has(rule.name.trim().toLowerCase())) // >=: due today but not yet created (the API creates it at 05:40 IST) still shows as coming
    .sort((a, b) => (a.date < b.date ? -1 : 1));
  return {
    key,
    general,
    generalTotal: sum(general),
    singles,
    groups: groupRows,
    total,
    isEmpty: general.length + singles.length + groupRows.length === 0,
    isFuture: key > monthOfDate(today),
    upcoming,
    upcomingTotal: upcoming.reduce((s, u) => s + u.rule.amount, 0),
  };
}

/** Sub-category totals of General spending, biggest first: [{ key, label, total }]. */
export function generalBreakdown(general) {
  const totals = {};
  for (const e of general) totals[e.category] = (totals[e.category] || 0) + e.amount;
  return Object.keys(totals)
    .sort((a, b) => totals[b] - totals[a])
    .map((key) => ({ key, label: categoryLabel(key), total: totals[key] }));
}

/** General spending grouped by date, newest day first: [{ date, total, rows }]. */
export function generalByDay(general, filterCategory = null) {
  const rows = filterCategory ? general.filter((e) => e.category === filterCategory) : general;
  const days = {};
  for (const e of rows) (days[e.date] = days[e.date] || []).push(e);
  return Object.keys(days)
    .sort((a, b) => (a < b ? 1 : -1))
    .map((date) => ({ date, total: sum(days[date]), rows: days[date].sort((a, b) => b.amount - a.amount) }));
}

/** Twelve month totals for the year grid, plus the year's total and the months with data. */
export function yearView(data, year) {
  const months = [];
  let spent = 0;
  let monthsWithData = 0;
  for (let m = 1; m <= 12; m++) {
    const v = monthView(data, year, m);
    months.push({ month: m, label: MONTHS_SHORT[m - 1], total: v.total, isEmpty: v.isEmpty });
    spent += v.total;
    if (!v.isEmpty) monthsWithData++;
  }
  const max = Math.max(1, ...months.map((m) => m.total));
  return { months: months.map((m) => ({ ...m, share: m.isEmpty ? 0 : Math.max(0.04, m.total / max) })), spent, monthsWithData, average: monthsWithData ? spent / monthsWithData : 0 };
}

/** Groups a new entry can join: those counted in the month of its date. */
export function groupsForDate(data, date) {
  const key = monthOfDate(date);
  return (data?.groups || []).filter((g) => g.month === key);
}

/** Entries whose name, note or category label contains the query (case-insensitive). */
export function searchExpenses(list, query) {
  const q = (query || "").trim().toLowerCase();
  if (!q) return list;
  return list.filter(
    (e) => (e.name || "").toLowerCase().includes(q) || (e.note || "").toLowerCase().includes(q) || categoryLabel(e.category).toLowerCase().includes(q)
  );
}

/** A sorted copy for the ledger table. key: date | name | category | amount; dir: asc | desc.
 *  Entries on the same date keep the order they were logged in (created_at),
 *  following the date direction — newest-logged first when dates run newest
 *  first — so two entries from one day never reshuffle alphabetically. */
export function sortExpenses(list, key = "date", dir = "desc") {
  const sign = dir === "asc" ? 1 : -1;
  const val = (e) =>
    key === "amount" ? e.amount : key === "name" ? (e.name || "").toLowerCase() : key === "category" ? categoryLabel(e.category).toLowerCase() : e.date;
  const dateSign = key === "date" ? sign : -1; // other columns tie-break newest first
  return [...list].sort((a, b) => {
    const x = val(a);
    const y = val(b);
    if (x < y) return -sign;
    if (x > y) return sign;
    if (a.date !== b.date) return a.date < b.date ? -dateSign : dateSign;
    const ca = a.created_at || "";
    const cb = b.created_at || "";
    if (ca !== cb) return ca < cb ? -dateSign : dateSign;
    return (a.name || "").localeCompare(b.name || "");
  });
}

/** The default sort direction when a column is first picked: text ascending, dates and money descending. */
export const defaultSortDir = (key) => (key === "name" || key === "category" ? "asc" : "desc");

/** The month as a Monday-first calendar: leading blanks, then one cell per day with that day's
 *  entries and total, padded to whole weeks. `level` 0..4 shades a day against the month's biggest
 *  day on a square-root scale, so one huge day does not flatten every other one. */
export function calendarMonth(year, month, entries) {
  const byDate = {};
  for (const e of entries) (byDate[e.date] = byDate[e.date] || []).push(e);
  const daysIn = new Date(year, month, 0).getDate();
  const lead = (new Date(year, month - 1, 1).getDay() + 6) % 7;
  const max = Math.max(1, ...Object.values(byDate).map(sum));
  const cells = [];
  for (let i = 0; i < lead; i++) cells.push({ key: `lead-${i}`, blank: true });
  for (let d = 1; d <= daysIn; d++) {
    const date = `${year}-${pad2(month)}-${pad2(d)}`;
    const rows = (byDate[date] || []).slice().sort((a, b) => b.amount - a.amount);
    const total = sum(rows);
    const f = Math.sqrt(total / max);
    cells.push({ key: date, date, day: d, rows, total, level: !total ? 0 : f <= 0.2 ? 1 : f <= 0.4 ? 2 : f <= 0.7 ? 3 : 4 });
  }
  while (cells.length % 7) cells.push({ key: `tail-${cells.length}`, blank: true });
  return { weekdays: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"], cells };
}

/** "₹89", "₹2.1k", "₹1.2L" — a calendar cell's figure. */
export function shortAmount(v) {
  if (v >= 100000) return `₹${(v / 100000).toFixed(1).replace(/\.0$/, "")}L`;
  if (v >= 1000) return `₹${(v / 1000).toFixed(1).replace(/\.0$/, "")}k`;
  return `₹${Math.round(v)}`;
}
